// This file renders a line chart for gem usage by category.
import { useEffect, useState, useRef } from "react";
import Chart from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";
Chart.register(ChartDataLabels);

const Graph = ({ gemUsage, isMobile=false }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const getTotalPerFeature = () => {
    const categories = ['Food', 'Shops', 'Services', 'Transit', 'Leisure'];
    if (gemUsage) {
      return categories.map(category => {
        const items = gemUsage[category] || {};
        return Object.values(items).reduce((acc, val) => acc + (val || 0), 0);
      });
    }
    return [];
  };

  const getRoundedMax = (max) => {
    if (max <= 10) return 10;
    const magnitude = Math.pow(10, Math.floor(Math.log10(max)));
    return Math.ceil(max / magnitude) * magnitude;
  };

  const getMaxValue = () => {
    const data = getTotalPerFeature();
    if (!data.length) return 10;
    const max = Math.max(...data);
    return getRoundedMax(max);
  };

  const generateCategoryTooltip = (category) => {
    const categoryData = gemUsage?.[category];
    if (categoryData) {
      return Object.entries(categoryData).map(([key, value]) => {
        return `${key}: ${value}`;
      }).join('\n');
    }
    return '';
  }

  const generateDatasets = () => {
    const data = getTotalPerFeature();
    if (!data.length || data.every(val => val === 0)) return [];
    return [
      {
        label: 'Dataset',
        data,
        borderColor: "#13283b",
        backgroundColor: "#13283b80",
        fill: true,
        pointStyle: 'circle',
        pointRadius: 15,
        pointHoverRadius: 20,
        pointBackgroundColor: "#13283b",
        datalabels: {
          labels: {
            value: {
              align: 'center',
              anchor: 'center',
              color: '#fff',
              font: {
                weight: 'bold',
              },
              formatter: (value) => value,
              display: true,
            },
            tooltip: {
              align: 'right',
              anchor: 'bottom',
              color: '#fff',
              backgroundColor: '#13283bb3',
              borderRadius: 6,
              padding: {
                left: 10,
                right: 10,
                top: 6,
                bottom: 6
              },
              font: {
                size: 12,
              },
              formatter: (value, context) => {
                const category = context.chart.data.labels[context.dataIndex];
                return generateCategoryTooltip(category);
              },
              offset: 32,
              clamp: true,
              display: (context) => {
              // Hide tooltip if value is 0 or isMobile is true
              if (isMobile) return false;
                return context.dataset.data[context.dataIndex] !== 0;
              },
            }
          }
        }
      }
    ];
  };

  useEffect(() => {
    const ctx = chartRef?.current?.getContext("2d");

    if (chartInstance.current) {
      chartInstance.current.data.datasets = generateDatasets();
      chartInstance.current.options.scales.y.max = getMaxValue();
      chartInstance.current.update();
    } else {
      const chartData = {
        labels: isMobile
          ? ['Food', 'Shops', 'Services', 'Transit', 'Leisure']
          : ['Food', 'Shops', 'Services', 'Transit', 'Leisure', ''],
        datasets: generateDatasets()
      };

      chartInstance.current = new Chart(ctx, {
        type: 'line',
        data: chartData,
        options: {
          responsive: true,
          plugins: {
            title: {
              display: false,
            },
            legend: {
              display: false
            },
            datalabels: {
              display: true
            },
            tooltip: {
              enabled: false
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              max: getMaxValue(),
            }
          }
        }
      });
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [gemUsage]);

  return (
    <div className="rounded-xl shadow-xl p-4">
      <canvas ref={chartRef} width="80%" height={isMobile ? '500px' : "50%"} />
    </div>
  )
};

export default Graph;